import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { contractAPI, api } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription} from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import {
  ArrowLeft, Plus, FileText, Edit, Power, PowerOff, Users, Building2, Eye
} from 'lucide-react';
import { toast } from 'sonner';

import { formatDateMonthDay } from '../../utils/timeFormat';
const ContractManager = () => {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    pdf_url: '',
    audience: 'talent',
    version: '1.0',
    is_required: true,
    content_html: ''
  });

  useEffect(() => {
    fetchContracts();
  }, []);

  const fetchContracts = async () => {
    try {
      const res = await contractAPI.adminList();
      setContracts(res.data);
    } catch (error) {
      toast.error('Failed to load contracts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title) {
      toast.error('Title is required');
      return;
    }

    try {
      if (editingContract) {
        await contractAPI.adminUpdate(editingContract.id, formData);
        toast.success('Contract updated');
      } else {
        await contractAPI.adminCreate({
          ...formData,
          pdf_url: formData.pdf_url || 'pending-upload'
        });
        toast.success('Contract created');
      }
      setShowForm(false);
      setEditingContract(null);
      resetForm();
      fetchContracts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save contract');
    }
  };

  const handleEdit = (contract) => {
    setEditingContract(contract);
    setFormData({
      title: contract.title,
      description: contract.description || '',
      pdf_url: contract.pdf_url || '',
      audience: contract.audience,
      version: contract.version,
      is_required: contract.is_required,
      content_html: contract.content_html || ''
    });
    setShowForm(true);
  };

  const toggleActive = async (contract) => {
    try {
      if (contract.is_active) {
        await contractAPI.adminDeactivate(contract.id);
        toast.success('Contract deactivated');
      } else {
        await contractAPI.adminActivate(contract.id);
        toast.success('Contract activated');
      }
      fetchContracts();
    } catch (error) {
      toast.error('Failed to update contract status');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      pdf_url: '',
      audience: 'talent',
      version: '1.0',
      is_required: true,
      content_html: ''
    });
  };

  const getAudienceIcon = (audience) => {
    if (audience === 'talent') return <Users className="w-4 h-4" />;
    if (audience === 'client') return <Building2 className="w-4 h-4" />;
    return <Eye className="w-4 h-4" />;
  };

  const getAudienceLabel = (audience) => {
    const labels = { talent: 'Talent', client: 'Client', both: 'All Users' };
    return labels[audience] || audience;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="contract-manager-page">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/admin/home')} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin
          </Button>
          <Button
            onClick={() => { resetForm(); setEditingContract(null); setShowForm(true); }}
            className="bg-cyan-500 hover:bg-cyan-600 text-white"
            data-testid="create-contract-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Contract
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Contract Templates</h1>
        <p className="text-gray-500 mb-8">
          Manage contract templates for talent agreements, direct booking agreements, and client service agreements.
          Upload your own contract language to replace default templates.
        </p>

        {/* Contract List */}
        <div className="space-y-4">
          {contracts.length === 0 ? (
            <Card className="bg-white border-gray-200">
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No contracts yet. Create your first contract template.</p>
              </CardContent>
            </Card>
          ) : (
            contracts.map((contract) => (
              <Card key={contract.id} className={`bg-white border-gray-200 ${!contract.is_active ? 'opacity-60' : ''}`} data-testid="contract-card">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <FileText className="w-5 h-5 text-cyan-500" />
                        <h3 className="font-semibold text-gray-900">{contract.title}</h3>
                        <Badge variant="outline" className="text-xs">v{contract.version}</Badge>
                        <Badge className={contract.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>
                          {contract.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                        {contract.is_required && (
                          <Badge className="bg-red-100 text-red-700">Required</Badge>
                        )}
                      </div>
                      {contract.description && (
                        <p className="text-sm text-gray-500 mb-2">{contract.description}</p>
                      )}
                      <div className="flex items-center gap-4 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                          {getAudienceIcon(contract.audience)}
                          {getAudienceLabel(contract.audience)}
                        </span>
                        <span>Created: {formatDateMonthDay(contract.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(contract)}
                        data-testid="edit-contract-btn"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleActive(contract)}
                        className={contract.is_active ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700'}
                        data-testid="toggle-contract-btn"
                      >
                        {contract.is_active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingContract ? 'Edit Contract' : 'Create Contract Template'}</DialogTitle>
          <DialogDescription className="sr-only">Dialog actions</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Talent Service Agreement"
                data-testid="contract-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this contract"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Audience *</Label>
                <Select value={formData.audience} onValueChange={(v) => setFormData({ ...formData, audience: v })}>
                  <SelectTrigger data-testid="audience-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="talent">Talent Only</SelectItem>
                    <SelectItem value="client">Client Only</SelectItem>
                    <SelectItem value="both">All Users</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Version</Label>
                <Input
                  value={formData.version}
                  onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                  placeholder="1.0"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>PDF Document</Label>
              <div className="flex gap-2">
                <Input
                  value={formData.pdf_url}
                  onChange={(e) => setFormData({ ...formData, pdf_url: e.target.value })}
                  placeholder="PDF URL (auto-filled on upload)"
                  className="flex-1"
                />
                <label className="cursor-pointer">
                  <input type="file" accept=".pdf" className="hidden" onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    const fd = new FormData();
                    fd.append('file', file);
                    try {
                      toast.loading('Uploading PDF...', { id: 'pdf-upload' });
                      const res = await api.post('/contracts/upload-pdf', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                      setFormData(prev => ({ ...prev, pdf_url: res.data.pdf_url }));
                      toast.dismiss('pdf-upload');
                      toast.success(`PDF uploaded (${res.data.size_kb}KB)`);
                    } catch {
                      toast.dismiss('pdf-upload');
                      toast.error('PDF upload failed');
                    }
                  }} />
                  <Button type="button" variant="outline" size="sm" asChild><span>Upload PDF</span></Button>
                </label>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Contract Content (HTML)</Label>
              <p className="text-xs text-gray-500">
                Paste your contract language here. This will be displayed to users during the signing process.
                You can use basic HTML formatting.
              </p>
              <Textarea
                value={formData.content_html}
                onChange={(e) => setFormData({ ...formData, content_html: e.target.value })}
                placeholder="Paste your contract content here..."
                rows={10}
                className="font-mono text-sm"
                data-testid="contract-content-textarea"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_required}
                onCheckedChange={(v) => setFormData({ ...formData, is_required: v })}
                data-testid="required-switch"
              />
              <Label>Required (users must accept before using the platform)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button
              className="bg-cyan-500 hover:bg-cyan-600 text-white"
              onClick={handleSubmit}
              data-testid="save-contract-btn"
            >
              {editingContract ? 'Update Contract' : 'Create Contract'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContractManager;
